import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, CreditCard, Sparkles, Loader2, Clock, ExternalLink, FileText, Download, Receipt, MapPin, Pencil } from "lucide-react";
import { toast } from "sonner";
import BillingAddressDialog from "@/components/BillingAddressDialog";

const API = process.env.REACT_APP_BACKEND_URL;
const CENTER = process.env.REACT_APP_BACKEND_URL; // origin returned to Stripe

const PLANS = [
  {
    key: "trademind_monthly",
    label: "Monatlich",
    price: "8,99 €",
    period: "pro Monat",
    hint: "Flexibel — jederzeit zum Monatsende kündbar.",
  },
  {
    key: "trademind_yearly",
    label: "Jährlich",
    price: "84,99 €",
    period: "pro Jahr",
    hint: "Sparst über 2 Monate im Vergleich zum Monats-Abo.",
    highlight: true,
  },
];

function fmtDaysLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

export default function BillingPanel() {
  const [plan, setPlan] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [billingDetails, setBillingDetails] = useState(null);
  const [addressOpen, setAddressOpen] = useState(false);
  const [pendingLookupKey, setPendingLookupKey] = useState(null);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    setInvoicesLoading(true);
    try {
      const [pR, iR, bR] = await Promise.all([
        axios.get(`${API}/api/billing/plan`, { withCredentials: true }),
        axios.get(`${API}/api/billing/invoices`, { withCredentials: true }).catch(() => ({ data: { invoices: [] } })),
        axios.get(`${API}/api/billing/billing-details`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setPlan(pR.data);
      setInvoices(iR.data?.invoices || []);
      setBillingDetails(bR.data);
    } catch (e) {
      toast.error("Konnte den aktuellen Plan nicht laden.");
    } finally {
      setLoading(false);
      setInvoicesLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Nach Stripe-Redirect: Session-ID im URL → Status pollen + Toast
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 8 && !cancelled; i++) {
        try {
          const r = await axios.get(`${API}/api/billing/status/${sid}`);
          if (r.data.payment_status === "paid") {
            toast.success("Willkommen in der Vollversion!", {
              description: "Deine Zahlung wurde bestätigt — alle Funktionen sind freigeschaltet.",
            });
            load();
            break;
          }
        } catch { /* retry */ }
        await new Promise((res) => setTimeout(res, 1500));
      }
      // URL aufräumen
      params.delete("session_id"); setParams(params, { replace: true });
    })();
    return () => { cancelled = true; };
  }, [params, setParams, load]);

  const startCheckout = async (lookupKey) => {
    // Rechnungsadresse muss vorhanden sein — sonst Dialog öffnen
    if (!billingDetails?.complete) {
      setPendingLookupKey(lookupKey);
      setAddressOpen(true);
      return;
    }
    setBusyKey(lookupKey);
    try {
      const r = await axios.post(`${API}/api/billing/checkout`,
        { lookup_key: lookupKey, origin_url: window.location.origin },
        { withCredentials: true });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error("Checkout konnte nicht gestartet werden", {
        description: e?.response?.data?.detail || "Bitte erneut versuchen.",
      });
      setBusyKey(null);
    }
  };

  const onAddressSaved = async () => {
    setAddressOpen(false);
    // Adresse frisch nachladen und danach den gemerkten Checkout starten
    try {
      const r = await axios.get(`${API}/api/billing/billing-details`, { withCredentials: true });
      setBillingDetails(r.data);
    } catch { /* ignore */ }
    if (pendingLookupKey) {
      const key = pendingLookupKey;
      setPendingLookupKey(null);
      // Direkt weitermachen — Adresse ist jetzt komplett
      setTimeout(() => startCheckoutDirect(key), 100);
    }
  };

  const startCheckoutDirect = async (lookupKey) => {
    setBusyKey(lookupKey);
    try {
      const r = await axios.post(`${API}/api/billing/checkout`,
        { lookup_key: lookupKey, origin_url: window.location.origin },
        { withCredentials: true });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error("Checkout konnte nicht gestartet werden", {
        description: e?.response?.data?.detail || "Bitte erneut versuchen.",
      });
      setBusyKey(null);
    }
  };

  const openPortal = async () => {
    setBusyKey("portal");
    try {
      const r = await axios.post(`${API}/api/billing/portal`,
        { origin_url: window.location.origin },
        { withCredentials: true });
      window.location.href = r.data.portal_url;
    } catch (e) {
      toast.error("Kundenportal konnte nicht geöffnet werden", {
        description: e?.response?.data?.detail || "",
      });
      setBusyKey(null);
    }
  };

  if (loading || !plan) {
    return <div className="text-sm text-muted-foreground" data-testid="billing-loading">Plan wird geladen…</div>;
  }

  const isFull = plan.plan === "vollversion";
  const daysLeft = fmtDaysLeft(plan.trial_ends_at);
  const trialActive = plan.trial_active && daysLeft !== null && daysLeft > 0;
  const subActive = plan.raw_plan === "vollversion";

  return (
    <div className="space-y-6" data-testid="billing-panel">
      {/* Status-Kachel */}
      <div className="card-surface p-5 md:p-6" data-testid="billing-status">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline">Aktueller Plan</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-heading text-xl">
                {isFull ? "Vollversion" : "Standardversion"}
              </span>
              {trialActive && (
                <Badge className="rounded-full bg-[hsl(43_74%_49%/0.2)] text-[hsl(43_74%_66%)] border border-[hsl(43_74%_49%/0.4)]">
                  <Clock className="h-3 w-3 mr-1" strokeWidth={1.5} />
                  Testphase — {daysLeft} Tag{daysLeft === 1 ? "" : "e"} übrig
                </Badge>
              )}
              {subActive && (
                <Badge className="rounded-full bg-[hsl(122_39%_49%/0.2)] text-[hsl(122_39%_65%)] border border-[hsl(122_39%_49%/0.4)]">
                  <CheckCircle2 className="h-3 w-3 mr-1" strokeWidth={1.5} />
                  Aktives Abo
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-2 max-w-lg">
              {isFull && subActive && "Du nutzt die volle TradeMind-Erfahrung inklusive Steuer-Tool und unbegrenzten Trades und Strategien."}
              {isFull && trialActive && "Deine 30-Tage-Testphase läuft. Danach wechselst du automatisch zurück in die Standardversion, sofern du kein Abo abschließt."}
              {!isFull && "Standardversion: max. 2 Strategien, 50 Trades pro Jahr, ohne Steuer-Tool. Für die vollen Funktionen wechsle jederzeit in die Vollversion."}
            </p>
          </div>
          {subActive && (
            <Button
              variant="outline"
              className="rounded-full h-11"
              onClick={openPortal}
              disabled={busyKey === "portal"}
              data-testid="billing-portal-btn"
            >
              {busyKey === "portal" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" strokeWidth={1.5} />}
              Zahlungsmethode & Kündigung
            </Button>
          )}
        </div>

        {/* Kontingent bei Standardversion */}
        {!isFull && plan.limits && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <UsageBar
              label="Trades in diesem Jahr"
              used={plan.usage.trades_this_year}
              max={plan.limits.trades_per_year_max}
              testid="usage-trades"
            />
            <UsageBar
              label="Gespeicherte Strategien"
              used={plan.usage.strategies}
              max={plan.limits.strategies_max}
              testid="usage-strategies"
            />
          </div>
        )}
      </div>

      {/* Upgrade-Karten — nur wenn nicht bereits abo-aktiv */}
      {!subActive && (
        <div>
          <div className="flex items-baseline gap-3 mb-4">
            <h3 className="font-heading text-lg">Vollversion freischalten</h3>
            <span className="text-xs text-muted-foreground">
              Bezahlung sicher über Stripe · monatlich kündbar · SEPA/Kreditkarte
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PLANS.map((p) => (
              <div
                key={p.key}
                className={`card-surface p-5 md:p-6 flex flex-col relative ${p.highlight ? "border-primary/60" : ""}`}
                data-testid={`plan-card-${p.key}`}
              >
                {p.highlight && (
                  <Badge className="absolute -top-3 left-5 rounded-full">
                    <Sparkles className="h-3 w-3 mr-1" strokeWidth={1.5} />
                    Beliebt
                  </Badge>
                )}
                <div className="overline">{p.label}</div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-heading text-3xl">{p.price}</span>
                  <span className="text-sm text-muted-foreground">{p.period}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2 flex-1">{p.hint}</p>
                <ul className="mt-4 space-y-2 text-sm">
                  {["Unbegrenzte Trades und Strategien", "Steuer-Tool mit PDF-Export", "Voller KI-Coach", "Broker-Sync für 5 Börsen"].map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-[hsl(122_39%_65%)] mt-0.5 shrink-0" strokeWidth={1.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="mt-5 rounded-full h-11"
                  onClick={() => startCheckout(p.key)}
                  disabled={busyKey === p.key}
                  data-testid={`plan-checkout-${p.key}`}
                >
                  {busyKey === p.key ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird geöffnet…</>
                  ) : (
                    <><CreditCard className="mr-2 h-4 w-4" strokeWidth={1.5} /> Jetzt {p.label.toLowerCase()} freischalten</>
                  )}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Nach dem Klick wirst du zu Stripe weitergeleitet. Zahlung mit
            <strong className="text-foreground mx-1">Kredit-/Debitkarte, SEPA-Lastschrift, PayPal oder Stripe Link</strong>
            möglich. Test-Karte für Sandbox:{" "}
            <code className="bg-secondary rounded px-1 py-0.5">4242 4242 4242 4242</code>, beliebiges Ablaufdatum in der Zukunft, beliebige CVC.
          </p>
        </div>
      )}

      {/* Rechnungs-Historie */}
      <InvoicesBlock invoices={invoices} loading={invoicesLoading} />

      {/* Rechnungsadresse (immer sichtbar, editierbar) */}
      <BillingAddressBlock
        details={billingDetails}
        onEdit={() => setAddressOpen(true)}
      />

      <BillingAddressDialog
        open={addressOpen}
        onOpenChange={(o) => { setAddressOpen(o); if (!o) setPendingLookupKey(null); }}
        onSaved={onAddressSaved}
        initial={billingDetails}
      />
    </div>
  );
}

function BillingAddressBlock({ details, onEdit }) {
  const complete = !!details?.complete;
  return (
    <div data-testid="billing-address-block">
      <div className="flex items-baseline gap-3 mb-4">
        <h3 className="font-heading text-lg flex items-center gap-2">
          <MapPin className="h-4 w-4" strokeWidth={1.5} /> Rechnungsadresse
        </h3>
        <span className="text-xs text-muted-foreground">
          Erscheint auf allen Rechnungen und wird nur für die Bezahlung genutzt.
        </span>
      </div>
      <div className="card-surface p-5 md:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            {complete ? (
              <div className="text-sm leading-relaxed" data-testid="billing-address-display">
                <div className="font-medium">{details.first_name} {details.last_name}</div>
                <div className="text-muted-foreground">{details.line1}</div>
                {details.line2 && <div className="text-muted-foreground">{details.line2}</div>}
                <div className="text-muted-foreground">{details.postal_code} {details.city}</div>
                <div className="text-muted-foreground">{details.country}</div>
                <div className="mt-3 text-xs text-muted-foreground font-mono">
                  Kundennummer: {details.customer_number}
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground max-w-md" data-testid="billing-address-empty">
                Noch keine Rechnungsadresse hinterlegt. Du wirst automatisch danach gefragt,
                sobald du ein Abo abschließt.
              </div>
            )}
          </div>
          <Button variant="outline" size="sm" className="rounded-full h-9" onClick={onEdit} data-testid="billing-address-edit">
            <Pencil className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} />
            {complete ? "Bearbeiten" : "Hinzufügen"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function InvoicesBlock({ invoices, loading }) {
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return "—"; }
  };
  const fmtEUR = (n, cur) => `${(n ?? 0).toFixed(2).replace(".", ",")} ${cur || "EUR"}`;
  const statusLabel = (s) => ({
    paid: { label: "Bezahlt", cls: "bg-[hsl(122_39%_49%/0.2)] text-[hsl(122_39%_65%)] border-[hsl(122_39%_49%/0.4)]" },
    open: { label: "Offen", cls: "bg-[hsl(43_74%_49%/0.2)] text-[hsl(43_74%_66%)] border-[hsl(43_74%_49%/0.4)]" },
    uncollectible: { label: "Ausstehend", cls: "bg-[hsl(0_66%_50%/0.15)] text-[hsl(0_66%_72%)] border-[hsl(0_66%_50%/0.3)]" },
  })[s] || { label: s, cls: "" };

  return (
    <div data-testid="invoices-block">
      <div className="flex items-baseline gap-3 mb-4">
        <h3 className="font-heading text-lg flex items-center gap-2">
          <Receipt className="h-4 w-4" strokeWidth={1.5} /> Rechnungen
        </h3>
        <span className="text-xs text-muted-foreground">
          Alle deine Stripe-Rechnungen — jederzeit als PDF herunterladbar
        </span>
      </div>

      {loading ? (
        <div className="card-surface p-6 text-sm text-muted-foreground text-center" data-testid="invoices-loading">
          Rechnungen werden geladen…
        </div>
      ) : invoices.length === 0 ? (
        <div className="card-surface p-8 text-center" data-testid="invoices-empty">
          <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Noch keine Rechnungen. Sobald du ein Abo abschließt, erscheinen hier alle Belege
            mit direktem PDF-Download.
          </p>
        </div>
      ) : (
        <div className="card-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Rechnungsnummer</th>
                <th className="px-4 py-3">Datum</th>
                <th className="px-4 py-3">Zeitraum</th>
                <th className="px-4 py-3 text-right">Betrag</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">PDF</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const st = statusLabel(inv.status);
                const amount = inv.status === "paid" ? inv.amount_paid : inv.amount_due;
                return (
                  <tr key={inv.id} className="border-t border-border hover:bg-secondary/20" data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{inv.number || inv.id.slice(0, 12)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(inv.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {inv.period_start && inv.period_end
                        ? `${fmtDate(inv.period_start)} – ${fmtDate(inv.period_end)}`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{fmtEUR(amount, inv.currency)}</td>
                    <td className="px-4 py-3">
                      <Badge className={`rounded-full text-[11px] border ${st.cls}`}>{st.label}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {inv.invoice_pdf && (
                          <a
                            href={inv.invoice_pdf}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 rounded-full border border-border h-8 px-3 text-xs hover:bg-secondary/60 transition-colors"
                            data-testid={`invoice-pdf-${inv.id}`}
                          >
                            <Download className="h-3.5 w-3.5" strokeWidth={1.5} /> PDF
                          </a>
                        )}
                        {inv.hosted_invoice_url && (
                          <a
                            href={inv.hosted_invoice_url}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                            data-testid={`invoice-view-${inv.id}`}
                            title="In Stripe öffnen"
                          >
                            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UsageBar({ label, used, max, testid }) {
  const pct = Math.min(100, Math.round((used / Math.max(max, 1)) * 100));
  const near = pct >= 80;
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-2 text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className={`font-mono ${near ? "text-[hsl(43_74%_66%)]" : "text-foreground"}`}>
          {used} / {max}
        </span>
      </div>
      <Progress value={pct} className={`h-1.5 ${near ? "[&>div]:bg-[hsl(43_74%_49%)]" : ""}`} />
      {near && (
        <p className="text-xs text-[hsl(43_74%_66%)] mt-1">
          Kontingent fast aufgebraucht — für unbegrenzte Nutzung Vollversion aktivieren.
        </p>
      )}
    </div>
  );
}
